import { useEffect } from 'react';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import WhatsAppButton from '../../home/components/WhatsAppButton';

export default function AracCheckupPage() {
  useSEO({
    title: 'Araç Check-Up ve Periyodik Kontrol | Denizli Araç Servisi',
    description: 'Denizli\'de kapsamlı araç check-up ve periyodik kontrol hizmetleri. 150+ kontrol noktası, elektronik teşhis, e-karne kaydı. 6 ayda bir düzenli bakım programı ile aracınızı koruyun.',
    keywords: 'denizli araç check-up, denizli periyodik bakım, denizli araç kontrolü, denizli araç muayene, denizli araç bakımı, araç sağlık kontrolü denizli',
    canonical: '/blog/arac-checkup-periyodik-kontrol',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Araç Check-Up & Periyodik Kontrol',
      description: 'Kapsamlı araç kontrolü ve 6 ayda bir periyodik bakım hizmeti. 150+ kontrol noktası ile aracınızın sağlığını koruyun.',
      author: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/5946f8dc6d1689a139aced3671fb829b/5627d08f05dce4eda6468c71266b611d.png',
        },
      },
      datePublished: '2024-01-15',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/arac-checkup-periyodik-kontrol`,
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/ab2af5a3d710a6da18296c4c35a7dce8.jpg"
          alt="Araç Check-Up"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl lg:text-6xl font-bold mb-6">Araç Check-Up &amp; Periyodik Kontrol</h1>
          <p className="font-sans text-xl text-gray-200">Kapsamlı araç kontrolü ve 6 ayda bir periyodik bakım hizmeti</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          {/* Introduction */}
          <div className="mb-16">
            <p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
              Aracınızın sağlığını korumak ve uzun ömürlü olmasını sağlamak için düzenli kontroller şarttır. Servisimizin sunduğu kapsamlı check-up hizmeti, aracınızın tüm sistemlerini detaylı olarak inceler ve olası sorunları erken tespit eder. 6 ayda bir periyodik kontrol programımız ile aracınız her zaman en iyi durumda olur.
            </p>
          </div>

          {/* Check-Up Nedir? */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Araç Check-Up Nedir?</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-4">
              Araç check-up, aracınızın tüm mekanik, elektronik ve güvenlik sistemlerinin profesyonel bir şekilde kontrol edilmesi işlemidir. Bu kapsamlı inceleme sayesinde:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Olası arızalar erken tespit edilir ve büyük masraflar önlenir</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Aracınızın güvenliği maksimum seviyede tutulur</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Yakıt tüketimi ve performans optimize edilir</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Aracın değeri korunur ve ikinci el satış değeri artar</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Tüm kontroller e-karneye kaydedilir</span>
              </li>
            </ul>
          </div>

          {/* Image */}
          <div className="mb-16 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=automotive%20mechanic%20performing%20detailed%20vehicle%20inspection%20with%20digital%20tablet%20and%20diagnostic%20tools%2C%20checking%20engine%20bay%20and%20components%2C%20professional%20workshop%20environment%2C%20comprehensive%20check-up%20process%20documentation&width=1200&height=600&seq=blog-checkup-002&orientation=landscape"
              alt="Araç Kontrolü"
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Check-Up Kapsamı */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Kapsamlı Check-Up İçeriği</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Check-up hizmetimiz, aracınızın tüm kritik noktalarını kapsar. 150'den fazla kontrol noktası içeren detaylı incelememiz:
            </p>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-settings-3-line text-accent-gold text-2xl mr-3"></i>
                  Motor ve Mekanik Sistem Kontrolü
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-9">
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Motor yağı seviye ve kalite kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Soğutma suyu kontrolü ve antifriz testi</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Triger kayış ve gergi rulman kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Motor sıkıştırma (kompresyon) testi</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Hava filtresi ve yakıt filtresi kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Egzoz sistemi ve emisyon kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Motor takozu ve askı kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Kayış ve kasnak sistemleri kontrolü</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-steering-2-line text-accent-gold text-2xl mr-3"></i>
                  Şasi ve Süspansiyon Kontrolü
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-9">
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Amortisör ve yay kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Rotil ve salıncak kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Aks ve diferansiyel kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Direksiyon kutusu ve pompası kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Rot ve balans ayarı kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Lastik durumu ve hava basıncı kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Fren diski ve balata kalınlığı ölçümü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Fren hidroliği ve hortum kontrolü</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-flashlight-line text-accent-gold text-2xl mr-3"></i>
                  Elektrik ve Elektronik Sistem Kontrolü
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-9">
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Akü durumu ve şarj sistemi testi</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Alternatör ve marş motoru kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Tüm aydınlatma sistemleri kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Buji ve ateşleme sistemi kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Sensör ve aktüatör kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>ECU hata kodu okuma ve analizi</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>ABS ve ESP sistemi kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Hava yastığı sistemi kontrolü</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-car-line text-accent-gold text-2xl mr-3"></i>
                  Kaporta ve İç Mekan Kontrolü
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-9">
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Kaporta ve boya durumu incelemesi</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Cam ve ayna sistemleri kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Silecek ve yıkama sistemi kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Klima sistemi performans testi</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Kabin filtresi kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Emniyet kemeri ve kilit sistemleri</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>Kapı ve bagaj menteşeleri kontrolü</span>
                  </div>
                  <div className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-checkbox-line text-accent-gold mr-2 mt-1"></i>
                    <span>İç döşeme ve koltuk durumu</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Periyodik Bakım */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">6 Ayda Bir Periyodik Bakım Programı</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Düzenli bakım, aracınızın ömrünü uzatmanın en etkili yoludur. 6 ayda bir periyodik bakım programımız:
            </p>
            <div className="bg-primary-dark rounded-2xl p-8 text-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-sans text-lg font-semibold mb-4 flex items-center">
                    <i className="ri-calendar-check-line text-accent-gold text-2xl mr-3"></i>
                    İlkbahar/Yaz Bakımı
                  </h4>
                  <ul className="space-y-2 ml-9">
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Klima sistemi kontrolü ve gazı</span>
                    </li>
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Soğutma sistemi detaylı kontrolü</span>
                    </li>
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Yaz lastiği kontrolü ve değişimi</span>
                    </li>
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Kabin filtresi değişimi</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-sans text-lg font-semibold mb-4 flex items-center">
                    <i className="ri-calendar-check-line text-accent-gold text-2xl mr-3"></i>
                    Sonbahar/Kış Bakımı
                  </h4>
                  <ul className="space-y-2 ml-9">
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Akü durumu ve şarj sistemi kontrolü</span>
                    </li>
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Antifriz seviye ve kalite kontrolü</span>
                    </li>
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Kış lastiği kontrolü ve değişimi</span>
                    </li>
                    <li className="font-sans text-sm text-gray-300 flex items-start">
                      <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                      <span>Isıtma sistemi kontrolü</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* E-Karne */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Dijital Araç E-Karnesi</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Tüm bakım ve kontrol işlemleriniz dijital ortamda kayıt altına alınır. E-karne sistemi ile:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-file-list-3-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Detaylı Kayıt</h4>
                <p className="font-sans text-sm text-gray-600">Yapılan tüm işlemler, değiştirilen parçalar ve kontrol sonuçları kaydedilir</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-history-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Geçmiş Takibi</h4>
                <p className="font-sans text-sm text-gray-600">Aracınızın tüm bakım geçmişine kolayca erişebilirsiniz</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-notification-3-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Hatırlatma Sistemi</h4>
                <p className="font-sans text-sm text-gray-600">Gelecek bakım zamanları için otomatik hatırlatma alırsınız</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-line-chart-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Değer Artışı</h4>
                <p className="font-sans text-sm text-gray-600">Düzenli bakım kayıtları, aracın ikinci el değerini artırır</p>
              </div>
            </div>
          </div>

          {/* Fiyatlandırma */}
          <div className="mb-16 bg-gray-50 rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6 text-center">Check-Up Paketlerimiz</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-accent-gold transition-colors">
                <h3 className="font-sans text-xl font-bold text-gray-900 mb-2">Temel Check-Up</h3>
                <p className="font-sans text-sm text-gray-600 mb-4">Rutin kontroller için ideal</p>
                <ul className="space-y-2 mb-6">
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>50+ kontrol noktası</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Temel sistem kontrolleri</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Detaylı rapor</span>
                  </li>
                </ul>
                <a href="/#appointment" className="block w-full bg-gray-900 text-white text-center py-3 rounded-lg font-sans font-semibold hover:bg-accent-gold hover:text-primary-dark transition-colors cursor-pointer whitespace-nowrap">
                  Randevu Al
                </a>
              </div>
              <div className="bg-white p-6 rounded-xl border-2 border-accent-gold relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-accent-gold text-primary-dark px-4 py-1 rounded-full font-sans text-sm font-bold whitespace-nowrap">
                  En Popüler
                </div>
                <h3 className="font-sans text-xl font-bold text-gray-900 mb-2">Kapsamlı Check-Up</h3>
                <p className="font-sans text-sm text-gray-600 mb-4">Detaylı inceleme isteyenler için</p>
                <ul className="space-y-2 mb-6">
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>100+ kontrol noktası</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Tüm sistem kontrolleri</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Elektronik teşhis</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>E-karne kaydı</span>
                  </li>
                </ul>
                <a href="/#appointment" className="block w-full bg-accent-gold text-primary-dark text-center py-3 rounded-lg font-sans font-semibold hover:bg-primary-dark hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  Randevu Al
                </a>
              </div>
              <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-accent-gold transition-colors">
                <h3 className="font-sans text-xl font-bold text-gray-900 mb-2">Premium Check-Up</h3>
                <p className="font-sans text-sm text-gray-600 mb-4">Üst segment araçlar için</p>
                <ul className="space-y-2 mb-6">
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>150+ kontrol noktası</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Tüm sistemler + detaylı analiz</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Marka özel teşhis</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Test sürüşü raporu</span>
                  </li>
                  <li className="font-sans text-sm text-gray-700 flex items-start">
                    <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                    <span>Ücretsiz danışmanlık desteği</span>
                  </li>
                </ul>
                <a href="/#appointment" className="block w-full bg-gray-900 text-white text-center py-3 rounded-lg font-sans font-semibold hover:bg-accent-gold hover:text-primary-dark transition-colors cursor-pointer whitespace-nowrap">
                  Randevu Al
                </a>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-primary-dark rounded-2xl p-10 text-center">
            <h3 className="font-serif text-3xl font-bold text-white mb-4">Aracınızın Sağlık Kontrolü Zamanı</h3>
            <p className="font-sans text-base text-gray-300 mb-8 max-w-2xl mx-auto">
              Kapsamlı check-up ile aracınızın durumunu öğrenin. Erken teşhis, büyük masrafları önler. Hemen randevu alın, aracınızı uzman ellerine teslim edin.
            </p>
            <a
              href="/#appointment"
              className="inline-block bg-accent-gold text-primary-dark px-8 py-4 rounded-lg font-sans font-semibold hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Ücretsiz Ön Değerlendirme
            </a>
          </div>

        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}